import { useState } from 'react';
import { Typography, useTheme, Box, Stack, Dialog, MenuItem, Skeleton } from '@mui/material';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { ATSReferenceCheckDataTestIds } from 'data-testids/ATS';
import { PrimaryButton, SecondaryButton } from 'sharedComponents/Buttons';
import {
  primaryButtonOverrideSx,
  secondaryButtonOverrideSx,
} from 'sharedComponents/Buttons/styles';
import { ATSAutomationCreateTestIds } from 'data-testids/ATS';
import { SuccessSnackbar, ErrorSnackbar } from 'sharedComponents/Snackbar';
import { ModalPaneContainer } from 'features/Automations/styles';
import { AutomationDropdown } from 'features/Automations/CreateAutomation/components/AutomationDropdown';
import { SuccessErrorAlertSeverity } from 'sharedComponents/Alert/types';
import auth from 'utils/auth';
import { ReferenceTemplate } from 'types/application';
import {
  useQueryGetSuperAndDistrictAdminsData,
  useQueryGetEmailTemplateListData,
  useQueryGetReferenceTemplateListData,
} from 'features/Automations/api/listQueries';
import { useQueryGetApplicationStatusListData } from 'features/Automations/api/createQueries';
import { appStatusType } from 'utils/enums';
import { UserBasic } from 'types/user';
import { ReferenceCheckModalProps, SelectOption, ReferenceCheckFormData } from './types';

const ReferenceCheckForm: React.FC<{
  onSuccess?: (formData: {
    emailTemplate: string;
    fromUser: string;
    referenceTemplate: string;
    candidateStatus: string;
    emailTemplateName: string;
    referenceTemplateName: string;
    fromUserName: string;
    candidateStatusName: string;
  }) => void;
  onClose: () => void;
}> = ({ onSuccess, onClose }) => {
  const theme = useTheme();
  const {
    handleSubmit,
    reset,
    formState: { isValid },
  } = useFormContext<ReferenceCheckFormData>();
  const [isLoading, setIsLoading] = useState(false);
  const [currentToast, setCurrentToast] = useState<SuccessErrorAlertSeverity | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const user = auth.getUser();
  const districtId: number | undefined = user?.profile?.district?.id;
  const { data: superAndDistrictAdmins, isLoading: isLoadingAdmins } =
    useQueryGetSuperAndDistrictAdminsData(districtId);
  const { data: emailTemplates, isLoading: isLoadingEmailTemplates } =
    useQueryGetEmailTemplateListData(user?.id);
  const { data: applicationStatuses, isLoading: isLoadingStatuses } =
    useQueryGetApplicationStatusListData(
      districtId,
      Object.values(appStatusType).filter(
        (status): status is number => typeof status === 'number' && status !== appStatusType.draft
      )
    );
  const { data: referenceTemplatesData, isLoading: isLoadingReferenceTemplates } =
    useQueryGetReferenceTemplateListData(districtId);

  const isLoadingData =
    isLoadingAdmins || isLoadingEmailTemplates || isLoadingStatuses || isLoadingReferenceTemplates;

  const userOptions: SelectOption[] = [
    { value: '', label: 'Select user', disabled: true },
    ...(superAndDistrictAdmins?.map((admin: UserBasic) => ({
      value: admin.id.toString(),
      label: admin.name,
    })) || []),
  ];

  const emailTemplateOptions: SelectOption[] = [
    { value: '', label: 'Select email template', disabled: true },
    ...(emailTemplates?.map((template) => ({
      value: template.id.toString(),
      label: template.title,
    })) || []),
  ];

  const candidateStatusOptions: SelectOption[] = [
    { value: '', label: 'Select candidate status', disabled: true },
    ...(applicationStatuses?.map((status) => ({
      value: status.id?.toString() || '',
      label: status.label || '',
    })) || []),
  ];

  const referenceTemplateOptions: SelectOption[] = [
    { value: '', label: 'Select reference check template', disabled: true },
    ...(referenceTemplatesData?.map((template: ReferenceTemplate) => ({
      value: template.id.toString(),
      label: template.title,
    })) || []),
  ];

  const getSenderName = (userId: string) => {
    const sender = superAndDistrictAdmins?.find((admin) => admin.id === Number(userId));
    return sender?.name;
  };

  const getEmailTemplateName = (templateId: string) => {
    const template = emailTemplates?.find((template) => template.id === Number(templateId));
    return template?.title;
  };

  const getReferenceTemplateName = (templateId: string) => {
    const template = referenceTemplatesData?.find((template) => template.id === Number(templateId));
    return template?.title;
  };

  const getCandidateStatusName = (statusId: string) => {
    const status = applicationStatuses?.find((status) => status.id === Number(statusId));
    return status?.label;
  };

  const mockApiCall = (): Promise<{ success: boolean; message?: string }> => {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        const shouldSucceed = Math.random() > 0.2;
        if (shouldSucceed) {
          resolve({ success: true, message: 'Reference check automation created successfully!' });
        } else {
          reject(new Error('Failed to create reference check automation. Please try again.'));
        }
      }, 2000);
    });
  };

  const onSubmit = async (data: ReferenceCheckFormData) => {
    setIsLoading(true);
    setCurrentToast(null);

    try {
      console.log('Creating reference check automation:', data);

      await mockApiCall();
      setCurrentToast('success');
      reset();

      const emailTemplateName = getEmailTemplateName(data.emailTemplate) || data.emailTemplate;
      const referenceTemplateName =
        getReferenceTemplateName(data.referenceTemplate) || data.referenceTemplate;
      const fromUserName = getSenderName(data.fromUser) || data.fromUser;
      const candidateStatusName =
        getCandidateStatusName(data.candidateStatus) || data.candidateStatus;

      onSuccess?.({
        emailTemplate: data.emailTemplate,
        fromUser: data.fromUser,
        referenceTemplate: data.referenceTemplate,
        candidateStatus: data.candidateStatus,
        emailTemplateName,
        referenceTemplateName,
        fromUserName,
        candidateStatusName,
      });

      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'An unexpected error occurred';
      setErrorMessage(errorMsg);
      setCurrentToast('error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    reset();
    onClose();
  };

  const modalContent = (
    <Stack spacing={3}>
      {isLoadingData ? (
        <>
          <Skeleton
            variant="rectangular"
            height={56}
            data-testid={ATSAutomationCreateTestIds.AUTOMATION_SKELETON}
          />
          <Skeleton
            variant="rectangular"
            height={56}
            data-testid={ATSAutomationCreateTestIds.AUTOMATION_SKELETON}
          />
          <Skeleton
            variant="rectangular"
            height={56}
            data-testid={ATSAutomationCreateTestIds.AUTOMATION_SKELETON}
          />
          <Skeleton
            variant="rectangular"
            height={56}
            data-testid={ATSAutomationCreateTestIds.AUTOMATION_SKELETON}
          />
        </>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(2) }}>
            <Typography
              variant="body1"
              sx={{ minWidth: '120px', color: theme.palette.text.primary }}
            >
              Send email
            </Typography>
            <AutomationDropdown
              dataTestId={ATSReferenceCheckDataTestIds.EMAIL_TEMPLATE_SELECT}
              name="emailTemplate"
              defaultValue=""
              sx={{ maxWidth: '400px', minWidth: '350px' }}
            >
              {emailTemplateOptions.map((option) => (
                <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
                  {option.label}
                </MenuItem>
              ))}
            </AutomationDropdown>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(2) }}>
            <Typography
              variant="body1"
              sx={{ minWidth: '120px', color: theme.palette.text.primary }}
            >
              from
            </Typography>
            <AutomationDropdown
              dataTestId={ATSReferenceCheckDataTestIds.FROM_USER_SELECT}
              name="fromUser"
              defaultValue=""
              sx={{ maxWidth: '400px', minWidth: '350px' }}
            >
              {userOptions.map((option) => (
                <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
                  {option.label}
                </MenuItem>
              ))}
            </AutomationDropdown>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(2) }}>
            <Typography
              variant="body1"
              sx={{ minWidth: '120px', color: theme.palette.text.primary }}
            >
              with reference check template
            </Typography>
            <AutomationDropdown
              dataTestId={ATSReferenceCheckDataTestIds.REFERENCE_TEMPLATE_SELECT}
              name="referenceTemplate"
              defaultValue=""
              sx={{ maxWidth: '400px', minWidth: '350px' }}
            >
              {referenceTemplateOptions.map((option) => (
                <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
                  {option.label}
                </MenuItem>
              ))}
            </AutomationDropdown>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(2) }}>
            <Typography
              variant="body1"
              sx={{ minWidth: '120px', color: theme.palette.text.primary }}
            >
              when candidate reaches the status
            </Typography>
            <AutomationDropdown
              dataTestId={ATSReferenceCheckDataTestIds.APPLICATION_STATUS_SELECT}
              name="candidateStatus"
              defaultValue=""
              sx={{ maxWidth: '400px', minWidth: '350px' }}
            >
              {candidateStatusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
                  {option.label}
                </MenuItem>
              ))}
            </AutomationDropdown>
          </Box>
        </>
      )}
    </Stack>
  );

  const ctaButtons = (
    <Box sx={{ pt: 3, display: 'flex', justifyContent: 'center', gap: theme.spacing(2) }}>
      <SecondaryButton
        dataTestId={ATSReferenceCheckDataTestIds.CANCEL_BUTTON}
        onClick={handleCancel}
        disabled={isLoading}
        sx={secondaryButtonOverrideSx(theme)}
      >
        Cancel
      </SecondaryButton>
      <PrimaryButton
        dataTestId={ATSReferenceCheckDataTestIds.CREATE_BUTTON}
        onClick={handleSubmit(onSubmit)}
        disabled={!isValid || isLoading || isLoadingData}
        isLoading={isLoading}
        sx={primaryButtonOverrideSx(theme)}
      >
        {isLoading ? 'Creating...' : 'Create'}
      </PrimaryButton>
    </Box>
  );

  return (
    <>
      <Typography color="black.dark" variant="h3" sx={{ textAlign: 'center' }}>
        Automated reference check
      </Typography>
      <ModalPaneContainer>
        {modalContent}
        {ctaButtons}
      </ModalPaneContainer>

      <SuccessSnackbar
        open={currentToast === 'success'}
        message="Successfully saved"
        onClose={() => setCurrentToast(null)}
        dataTestId={ATSReferenceCheckDataTestIds.SUCCESS_TOAST}
        sx={{
          '& .MuiAlert-root': {
            padding: `${theme.spacing(0.75)} ${theme.spacing(2)}`,
            borderRadius: theme.spacing(0.5),
          },
        }}
      />

      <ErrorSnackbar
        open={currentToast === 'error'}
        message={errorMessage}
        onClose={() => setCurrentToast(null)}
        dataTestId={ATSReferenceCheckDataTestIds.ERROR_TOAST}
        sx={{
          '& .MuiAlert-root': {
            padding: `${theme.spacing(0.75)} ${theme.spacing(2)}`,
            borderRadius: theme.spacing(0.5),
          },
        }}
      />
    </>
  );
};

export const ReferenceCheckModal: React.FC<ReferenceCheckModalProps> = ({
  open,
  onClose,
  jobId: _jobId,
  onSuccess,
}) => {
  const theme = useTheme();
  const methods = useForm<ReferenceCheckFormData>({
    defaultValues: {
      emailTemplate: '',
      fromUser: '',
      referenceTemplate: '',
      candidateStatus: '',
    },
    mode: 'onChange',
  });

  const handleClose = () => {
    methods.reset();
    onClose();
  };

  return (
    <Dialog
      maxWidth={'sm'}
      fullWidth
      data-testid={ATSReferenceCheckDataTestIds.REFERENCE_CHECK_MODAL}
      onClose={handleClose}
      open={open}
      sx={{
        '.MuiPaper-root': {
          borderRadius: theme.spacing(2),
          padding: 4,
        },
      }}
    >
      <FormProvider {...methods}>
        <ReferenceCheckForm onSuccess={onSuccess} onClose={handleClose} />
      </FormProvider>
    </Dialog>
  );
};
